import invariant from "tiny-invariant";
import { isMatching, P } from "ts-pattern";

import type { TCorrection, TPatient } from "~/types/patient";

import { isNone } from "./assert";
import { Status } from "./courses";

export function getLastCorrection(patient: TPatient): TCorrection {
  const lastCorrection = patient.course.correction[patient.course.correction.length - 1];
  // patient should always have at least one correction, so this should never raise an error
  invariant(lastCorrection, `patient_id: ${patient.patient_id} - no last correction found`);
  return lastCorrection;
}

export function getLastSubmittedCorrection(patient: TPatient): TCorrection {
  const lastCorrection = getLastCorrection(patient);

  if (hasUnfilledCorrection(patient)) {
    const lastSubmittedCorrection = patient.course.correction[patient.course.correction.length - 2];
    invariant(
      lastSubmittedCorrection,
      `patient_id: ${patient.patient_id} - no last submitted correction found`,
    );
    return lastSubmittedCorrection;
  }

  return lastCorrection;
}

export function hasUnfilledCorrection(patient: TPatient): boolean {
  const lastCorrection = getLastCorrection(patient);
  return patient.status == Status.UNFILLED_CASE && lastCorrection.index > 0;
}

const patientPattern = {
  patient_id: P.number,
  course: {
    course_id: P.union(P.number, "None"),
  },
};

/**
 * Use this function to determine if value is a patient object.
 */
export function isPatient(value: unknown): value is TPatient {
  return isMatching(patientPattern, value);
}

export function assertIsPatient(value: unknown): asserts value is TPatient {
  invariant(isPatient(value), "values is not a valid patient object");
}

export function getLatestStepsCountCompleted(patient: TPatient): number | undefined {
  for (const correction of patient.course.correction.slice().reverse()) {
    if (!isNone(correction.steps_count_completed)) {
      return correction.steps_count_completed;
    }
  }

  return undefined;
}

export function getLatestStepsCountTotal(patient: TPatient): number | undefined {
  for (const correction of patient.course.correction.slice().reverse()) {
    if (!isNone(correction.steps_count_total)) {
      return correction.steps_count_total;
    }
  }

  return undefined;
}
